import { useCallback } from 'react';
import {
  Grid,
  GridItem,
  Text,
  VStack,
  HStack,
  Checkbox,
  Select,
} from '@chakra-ui/react';

import {
  useAnswerCodes,
  useHabitationalLiabilityLimitConfirmation,
} from '../../hooks';
import { ValidatedField } from '..';
import HabitationalLiabilityWarningModal from '../HabitationalLiabilityWarningModal';
import HabitationalUnderwritingApprovalModal from '../HabitationalUnderwritingApprovalModal';
import {
  COVERAGE_DEDUCTIBLE_OPTIONS,
  HABITATIONAL_LIABILITY_LIMIT_OPTIONS,
} from '../../constants/habitationalCoveragesOptions';
import type { CoverageSectionProps } from './types';

interface CoverageBasicsSectionProps extends CoverageSectionProps {
  quoteId: number;
  province: number;
}

const CoverageBasicsSection = ({
  coverages,
  updateCoverage,
  quoteId,
  province,
}: CoverageBasicsSectionProps) => {
  const {
    getOptions,
    getCodeByLabel,
    isLoading: isLoadingCodes,
  } = useAnswerCodes();

  const comprehensiveCode = getCodeByLabel(
    'Comprehensive',
    'location_form_type',
  );

  const handleLiabilityLimitApply = useCallback(
    (newValue: number | null) => {
      updateCoverage('liability_limit_cad', newValue);
    },
    [updateCoverage],
  );

  const {
    handleLiabilityLimitChange,
    warningModalProps,
    underwritingModalProps,
  } = useHabitationalLiabilityLimitConfirmation({
    quoteId,
    province,
    currentLiabilityLimit: coverages.liability_limit_cad,
    onApplyChange: handleLiabilityLimitApply,
  });

  return (
    <>
      <Grid templateColumns="1fr 1fr" gap={6}>
        <GridItem>
          <Text fontWeight="700" mb={2}>
            Coverage
          </Text>
          <HStack spacing={4} align="flex-end" wrap="wrap">
            <ValidatedField
              itemPath="location"
              fieldPath="coverages.coverage_form_type"
              label="Coverage"
              labelSize="xs"
              formControlProps={{ w: '160px' }}
            >
              <Select
                variant="main"
                size="sm"
                height={30}
                value={coverages.coverage_form_type || comprehensiveCode}
                onChange={(e) =>
                  updateCoverage('coverage_form_type', e.target.value)
                }
                isDisabled={isLoadingCodes}
              >
                {getOptions('location_form_type')
                  .filter((opt) => !opt.label.includes('[SK only]'))
                  .map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
              </Select>
            </ValidatedField>
            <ValidatedField
              itemPath="location/coverages"
              fieldPath="coverages.deductible_cad"
              label="Deductible"
              labelSize="xs"
              formControlProps={{ w: '100px' }}
            >
              <Select
                variant="main"
                size="sm"
                height={30}
                value={coverages.deductible_cad ?? ''}
                onChange={(e) =>
                  updateCoverage(
                    'deductible_cad',
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
              >
                <option value="">Select...</option>
                {COVERAGE_DEDUCTIBLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </ValidatedField>
            <VStack spacing={1} align="flex-start">
              <Checkbox
                isChecked={coverages.grbc_enabled}
                onChange={(e) =>
                  updateCoverage('grbc_enabled', e.target.checked)
                }
                size="md"
                colorScheme="blue"
              >
                <Text fontSize="sm">GRBC</Text>
              </Checkbox>
              <Checkbox
                isChecked={coverages.single_limit_enabled}
                onChange={(e) =>
                  updateCoverage('single_limit_enabled', e.target.checked)
                }
                size="md"
                colorScheme="blue"
              >
                <Text fontSize="sm">Single Limit</Text>
              </Checkbox>
              <Checkbox
                isChecked={coverages.claim_free_protection_endorsement}
                onChange={(e) =>
                  updateCoverage(
                    'claim_free_protection_endorsement',
                    e.target.checked,
                  )
                }
                size="md"
                colorScheme="blue"
              >
                <Text fontSize="sm">Claim Free Protection</Text>
              </Checkbox>
            </VStack>
          </HStack>
        </GridItem>
        <GridItem>
          <Text fontWeight="700" mb={2}>
            Liability
          </Text>
          <Text fontSize="sm" color="gray.600" mb={2}>
            Bodily Injury / Property Damage
          </Text>
          <HStack spacing={4}>
            <ValidatedField
              itemPath="liability"
              fieldPath="coverages.liability_limit_cad"
              label="Limit"
              labelSize="xs"
              formControlProps={{ w: '130px' }}
            >
              <Select
                variant="main"
                size="sm"
                height={30}
                value={coverages.liability_limit_cad ?? ''}
                onChange={(e) =>
                  handleLiabilityLimitChange(
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
              >
                <option value="">Select...</option>
                {HABITATIONAL_LIABILITY_LIMIT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </ValidatedField>
          </HStack>
        </GridItem>
      </Grid>

      <HabitationalLiabilityWarningModal {...warningModalProps} />
      <HabitationalUnderwritingApprovalModal {...underwritingModalProps} />
    </>
  );
};

export default CoverageBasicsSection;
